import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Webhook, Lock, Save, Loader2, CheckCircle2, Calculator, FileText, FileWarning, Mic, Kanban, Banknote, ClipboardSignature, Upload, Send, Search, User, FilePlus2, Sparkles, AlertOctagon, ArrowRightLeft, Settings2, Wallet } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';
import ParcelamentoConfigCard from './components/ParcelamentoConfigCard';

// Aba Custos carrega sob demanda: gráficos e planilha só entram quando alguém abre.
const CustosApisView = lazy(() => import('./CustosApis/CustosApisView'));

const ApisView = () => {
    // Aba na URL (#/config/apis?aba=custos) para dar para mandar o link direto.
    const [params, setParams] = useSearchParams();
    const aba = params.get('aba') === 'custos' ? 'custos' : 'config';
    const trocarAba = (nova: 'config' | 'custos') => setParams(nova === 'custos' ? { aba: 'custos' } : {}, { replace: true });

    const { user } = useAuth();
    // State for Voice Webhook
    const [webhookUrl, setWebhookUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);

    // State for Negotiation Webhook
    const [negotiationWebhookUrl, setNegotiationWebhookUrl] = useState('');
    const [savingNeg, setSavingNeg] = useState(false);
    const [successNeg, setSuccessNeg] = useState(false);

    // State for Simulation Webhook
    const [simulationWebhookUrl, setSimulationWebhookUrl] = useState('');
    const [savingSim, setSavingSim] = useState(false);
    const [successSim, setSuccessSim] = useState(false);

    // State for Boleto Emission Webhook (unified — n8n IF node routes internally)
    const [boletoWebhookUrl, setBoletoWebhookUrl] = useState('');
    const [savingBoleto, setSavingBoleto] = useState(false);
    const [successBoleto, setSuccessBoleto] = useState(false);

    // State for Boleto Send Webhook
    const [boletoSendWebhookUrl, setBoletoSendWebhookUrl] = useState('');
    const [savingBoletoSend, setSavingBoletoSend] = useState(false);
    const [successBoletoSend, setSuccessBoletoSend] = useState(false);

    // State for Fatura Skip Send Webhook
    const [faturaSkipSendWebhookUrl, setFaturaSkipSendWebhookUrl] = useState('');
    const [savingFaturaSkipSend, setSavingFaturaSkipSend] = useState(false);
    const [successFaturaSkipSend, setSuccessFaturaSkipSend] = useState(false);

    // State for Contract Emission Webhook
    const [contractWebhookUrl, setContractWebhookUrl] = useState('');
    const [savingContract, setSavingContract] = useState(false);
    const [successContract, setSuccessContract] = useState(false);

    // State for Boleto Cancellation Webhook
    const [boletoCancellationWebhookUrl, setBoletoCancellationWebhookUrl] = useState('');
    const [savingBoletoCancel, setSavingBoletoCancel] = useState(false);
    const [successBoletoCancel, setSuccessBoletoCancel] = useState(false);

    const [boletoProrrogacaoWebhookUrl, setBoletoProrrogacaoWebhookUrl] = useState('');
    const [savingBoletoProrrogacao, setSavingBoletoProrrogacao] = useState(false);
    const [successBoletoProrrogacao, setSuccessBoletoProrrogacao] = useState(false);

    // State for Sicoob Consulta Webhook
    const [sicoobConsultaWebhookUrl, setSicoobConsultaWebhookUrl] = useState('');
    const [savingSicoobConsulta, setSavingSicoobConsulta] = useState(false);
    const [successSicoobConsulta, setSuccessSicoobConsulta] = useState(false);
    // State for Sicoob Consulta Pagador Webhook
    const [sicoobConsultaPagadorWebhookUrl, setSicoobConsultaPagadorWebhookUrl] = useState('');
    const [savingSicoobConsultaPagador, setSavingSicoobConsultaPagador] = useState(false);
    const [successSicoobConsultaPagador, setSuccessSicoobConsultaPagador] = useState(false);

    // State for Boleto Único — Pagamento Equatorial Webhook
    const [boletoUnicoPagamentoEquatorialWebhookUrl, setBoletoUnicoPagamentoEquatorialWebhookUrl] = useState('');
    const [savingBoletoUnicoPagamentoEquatorial, setSavingBoletoUnicoPagamentoEquatorial] = useState(false);
    const [successBoletoUnicoPagamentoEquatorial, setSuccessBoletoUnicoPagamentoEquatorial] = useState(false);

    // State for PDF Invoice Webhook
    const [pdfInvoiceWebhookUrl, setPdfInvoiceWebhookUrl] = useState('');
    const [savingPdfInvoice, setSavingPdfInvoice] = useState(false);
    const [successPdfInvoice, setSuccessPdfInvoice] = useState(false);

    // State for CRM Invoice Upload Webhook
    const [crmFaturaWebhookUrl, setCrmFaturaWebhookUrl] = useState('');
    const [savingCrmFatura, setSavingCrmFatura] = useState(false);
    const [successCrmFatura, setSuccessCrmFatura] = useState(false);

    // State for CRM Documento Upload Webhook
    const [crmDocumentoWebhookUrl, setCrmDocumentoWebhookUrl] = useState('');
    const [savingCrmDocumento, setSavingCrmDocumento] = useState(false);
    const [successCrmDocumento, setSuccessCrmDocumento] = useState(false);

    // State for PDF Definitivo Webhook
    const [pdfDefinitivoWebhookUrl, setPdfDefinitivoWebhookUrl] = useState('');
    const [savingPdfDefinitivo, setSavingPdfDefinitivo] = useState(false);
    const [successPdfDefinitivo, setSuccessPdfDefinitivo] = useState(false);

    // State for Termination Term Webhook
    const [cancellationWebhookUrl, setCancellationWebhookUrl] = useState('');
    const [savingCancel, setSavingCancel] = useState(false);
    const [successCancel, setSuccessCancel] = useState(false);

    // State for Aditivo Contratual Webhook
    const [aditivoWebhookUrl, setAditivoWebhookUrl] = useState('');
    const [savingAditivo, setSavingAditivo] = useState(false);
    const [successAditivo, setSuccessAditivo] = useState(false);

    // State for Comunicado de Advertência Webhook
    const [advertenciaWebhookUrl, setAdvertenciaWebhookUrl] = useState('');
    const [savingAdvertencia, setSavingAdvertencia] = useState(false);
    const [successAdvertencia, setSuccessAdvertencia] = useState(false);

    // State for Notificação de Mora Webhook
    const [moraWebhookUrl, setMoraWebhookUrl] = useState('');
    const [savingMora, setSavingMora] = useState(false);
    const [successMora, setSuccessMora] = useState(false);

    // State for Troca de Titularidade Webhook
    const [trocaTitularidadeWebhookUrl, setTrocaTitularidadeWebhookUrl] = useState('');
    const [savingTrocaTitularidade, setSavingTrocaTitularidade] = useState(false);
    const [successTrocaTitularidade, setSuccessTrocaTitularidade] = useState(false);

    // State for Lemit (webhook proxy n8n + cost per query)
    const [lemitWebhookUrl, setLemitWebhookUrl] = useState('');
    const [lemitCustoPessoa, setLemitCustoPessoa] = useState('');
    const [lemitCustoEmpresa, setLemitCustoEmpresa] = useState('');
    // Fila noturna da Prospecção (Lista Fria) — ver src/services/prospeccaoFila.ts
    const [lemitLimiteDiario, setLemitLimiteDiario] = useState('500');
    const [prospeccaoJanelaInicio, setProspeccaoJanelaInicio] = useState('22:00');
    const [prospeccaoJanelaFim, setProspeccaoJanelaFim] = useState('23:59');
    const [savingLemitCusto, setSavingLemitCusto] = useState(false);
    const [successLemitCusto, setSuccessLemitCusto] = useState(false);

    // State for Casa dos Dados — descoberta de CNPJ (custo + teto mensal + gasto do mês)
    const [cddCusto, setCddCusto] = useState('');
    const [cddLimite, setCddLimite] = useState('');
    const [cddGastoMes, setCddGastoMes] = useState<number | null>(null);
    const [savingCdd, setSavingCdd] = useState(false);
    const [successCdd, setSuccessCdd] = useState(false);

    // State for Geradoras — fator de geração esperada (kWh/kWp/dia), usado na
    // projeção "Real vs. Esperado" do Painel Executivo. Fallback de 4,5 no
    // código se a chave nunca foi salva (ver useGeradorasDashboardData.ts).
    const [geradorasFatorGeracao, setGeradorasFatorGeracao] = useState('');
    const [savingGeradorasFator, setSavingGeradorasFator] = useState(false);
    const [successGeradorasFator, setSuccessGeradorasFator] = useState(false);

    // State for Rateio Protocolo Webhook (Módulo de Rateio — envio da planilha à distribuidora)
    const [rateioProtocoloWebhookUrl, setRateioProtocoloWebhookUrl] = useState('');
    const [savingRateioProtocolo, setSavingRateioProtocolo] = useState(false);
    const [successRateioProtocolo, setSuccessRateioProtocolo] = useState(false);

    // State for Rateio Protocolo — e-mail principal que recebe cópia interna do envio
    const [emailPrincipalRateio, setEmailPrincipalRateio] = useState('vinicius@vigorenergy.com.br');
    const [savingEmailPrincipalRateio, setSavingEmailPrincipalRateio] = useState(false);
    const [successEmailPrincipalRateio, setSuccessEmailPrincipalRateio] = useState(false);

    // State for Rateio Espelho Webhook (Módulo de Rateio — cobrança automática de espelhos, 1x/15 dias)
    const [rateioEspelhoWebhookUrl, setRateioEspelhoWebhookUrl] = useState('');
    const [savingRateioEspelho, setSavingRateioEspelho] = useState(false);
    const [successRateioEspelho, setSuccessRateioEspelho] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            // Fetch Voice Webhook
            const { data } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'voice_transcription_webhook_url')
                .single();

            if (data) {
                setWebhookUrl(data.value || '');
            }

            // Fetch Negotiation Webhook
            const { data: negData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'n8n_negotiation_webhook_url')
                .single();

            if (negData) {
                setNegotiationWebhookUrl(negData.value || '');
            }

            // Fetch Simulation Webhook
            const { data: simData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'simulation_webhook_url')
                .single();

            if (simData) {
                setSimulationWebhookUrl(simData.value || '');
            }

            // Fetch Boleto Emission Webhook (unified)
            const { data: boletoData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'boleto_emission_webhook_url')
                .single();

            if (boletoData) {
                setBoletoWebhookUrl(boletoData.value || '');
            }

            // Fetch Boleto Send Webhook
            const { data: boletoSendData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'boleto_send_webhook_url')
                .single();

            if (boletoSendData) {
                setBoletoSendWebhookUrl(boletoSendData.value || '');
            }

            // Fetch Rateio Protocolo Webhook
            const { data: rateioProtocoloData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'rateio_protocolo_webhook_url')
                .single();

            if (rateioProtocoloData) {
                setRateioProtocoloWebhookUrl(rateioProtocoloData.value || '');
            }

            // Fetch Rateio Protocolo — e-mail principal
            const { data: emailPrincipalRateioData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'rateio_protocolo_email_principal')
                .single();

            if (emailPrincipalRateioData?.value) {
                setEmailPrincipalRateio(emailPrincipalRateioData.value);
            }

            // Fetch Rateio Espelho Webhook
            const { data: rateioEspelhoData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'rateio_espelho_webhook_url')
                .single();

            if (rateioEspelhoData) {
                setRateioEspelhoWebhookUrl(rateioEspelhoData.value || '');
            }

            // Fetch Fatura Skip Send Webhook
            const { data: faturaSkipSendData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'fatura_skip_send_webhook_url')
                .single();

            if (faturaSkipSendData) {
                setFaturaSkipSendWebhookUrl(faturaSkipSendData.value || '');
            }

            // Fetch Boleto Cancellation Webhook
            const { data: boletoCancelData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'boleto_cancellation_webhook_url')
                .single();

            if (boletoCancelData) {
                setBoletoCancellationWebhookUrl(boletoCancelData.value || '');
            }

            // Fetch Boleto Prorrogação Webhook
            const { data: boletoProrrogacaoData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'boleto_prorrogacao_webhook_url')
                .single();

            if (boletoProrrogacaoData) {
                setBoletoProrrogacaoWebhookUrl(boletoProrrogacaoData.value || '');
            }

            // Fetch Sicoob Consulta Webhook
            const { data: sicoobConsultaData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'sicoob_consulta_webhook_url')
                .single();

            if (sicoobConsultaData) {
                setSicoobConsultaWebhookUrl(sicoobConsultaData.value || '');
            }

            // Fetch Sicoob Consulta Pagador Webhook
            const { data: sicoobConsultaPagadorData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'sicoob_consulta_pagador_webhook_url')
                .single();

            if (sicoobConsultaPagadorData) {
                setSicoobConsultaPagadorWebhookUrl(sicoobConsultaPagadorData.value || '');
            }

            // Fetch Boleto Único — Pagamento Equatorial Webhook
            const { data: boletoUnicoPagamentoEquatorialData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'boleto_unico_pagamento_equatorial_webhook_url')
                .single();

            if (boletoUnicoPagamentoEquatorialData) {
                setBoletoUnicoPagamentoEquatorialWebhookUrl(boletoUnicoPagamentoEquatorialData.value || '');
            }

            // Fetch Contract Emission Webhook
            const { data: contractData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'issuance_of_terms_of_agreement')
                .single();

            if (contractData) {
                setContractWebhookUrl(contractData.value || '');
            }

            // Fetch Document Upload Webhook
            const { data: docData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'documents_upload_webhook_url')
                .single();

            if (docData) {
                setDocumentUploadWebhookUrl(docData.value || '');
            }

            // Fetch PDF Invoice Webhook
            const { data: pdfData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'pdf_invoice_webhook_url')
                .single();

            if (pdfData) {
                setPdfInvoiceWebhookUrl(pdfData.value || '');
            }

            // Fetch CRM Invoice Upload Webhook
            const { data: crmFaturaData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'crm_fatura_upload_webhook_url')
                .single();

            if (crmFaturaData) {
                setCrmFaturaWebhookUrl(crmFaturaData.value || '');
            }

            // Fetch CRM Documento Upload Webhook
            const { data: crmDocumentoData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'crm_documento_upload_webhook_url')
                .single();

            if (crmDocumentoData) {
                setCrmDocumentoWebhookUrl(crmDocumentoData.value || '');
            }

            // Fetch PDF Definitivo Webhook
            const { data: pdfDefData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'pdf_definitivo_webhook_url')
                .single();

            if (pdfDefData) {
                setPdfDefinitivoWebhookUrl(pdfDefData.value || '');
            }

            // Fetch Cancellation Webhook
            const { data: cancelData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'cancellation_term_webhook_url')
                .single();

            if (cancelData) {
                setCancellationWebhookUrl(cancelData.value || '');
            }

            // Fetch Aditivo Contratual Webhook
            const { data: aditivoData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'aditivo_contratual_webhook_url')
                .single();

            if (aditivoData) {
                setAditivoWebhookUrl(aditivoData.value || '');
            }

            // Fetch Comunicado de Advertência Webhook
            const { data: advertenciaData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'comunicado_advertencia_webhook_url')
                .single();

            if (advertenciaData) {
                setAdvertenciaWebhookUrl(advertenciaData.value || '');
            }

            // Fetch Notificação de Mora Webhook
            const { data: moraData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'notificacao_mora_webhook_url')
                .single();

            if (moraData) {
                setMoraWebhookUrl(moraData.value || '');
            }

            // Fetch Troca de Titularidade Webhook
            const { data: trocaTitularidadeData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'troca_titularidade_webhook_url')
                .single();

            if (trocaTitularidadeData) {
                setTrocaTitularidadeWebhookUrl(trocaTitularidadeData.value || '');
            }

            // Fetch Lemit webhook (proxy n8n) + cost per query
            const { data: lemitWebhookData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'lemit_webhook_url')
                .single();
            if (lemitWebhookData) setLemitWebhookUrl(lemitWebhookData.value || '');

            const { data: lemitPessoaData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'lemit_custo_pessoa')
                .single();
            if (lemitPessoaData) setLemitCustoPessoa(lemitPessoaData.value || '');

            const { data: lemitEmpresaData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'lemit_custo_empresa')
                .single();
            if (lemitEmpresaData) setLemitCustoEmpresa(lemitEmpresaData.value || '');

            // Fila noturna da Prospecção: teto diário da conta Lemit e a janela em
            // que o worker pode rodar (a Lista Fria enfileira, o cron processa).
            const { data: filaRows } = await supabase
                .from('system_settings').select('key, value')
                .in('key', ['lemit_limite_diario', 'prospeccao_janela_inicio', 'prospeccao_janela_fim']);
            for (const r of filaRows || []) {
                if (r.key === 'lemit_limite_diario') setLemitLimiteDiario(r.value || '500');
                if (r.key === 'prospeccao_janela_inicio') setProspeccaoJanelaInicio(r.value || '22:00');
                if (r.key === 'prospeccao_janela_fim') setProspeccaoJanelaFim(r.value || '23:59');
            }

            // Casa dos Dados — custo, teto mensal e gasto do mês (descoberta de CNPJ)
            const { data: cddRows } = await supabase
                .from('system_settings').select('key, value')
                .in('key', ['casadosdados_custo_consulta', 'casadosdados_limite_mensal']);
            for (const r of cddRows || []) {
                if (r.key === 'casadosdados_custo_consulta') setCddCusto(r.value || '');
                if (r.key === 'casadosdados_limite_mensal') setCddLimite(r.value || '');
            }
            const inicioMes = new Date(); inicioMes.setDate(1); inicioMes.setHours(0, 0, 0, 0);
            const { data: cddGastos } = await supabase
                .from('cdd_consultas').select('custo').gte('created_at', inicioMes.toISOString());
            if (cddGastos) setCddGastoMes(cddGastos.reduce((s: number, r: any) => s + Number(r.custo || 0), 0));

            // Geradoras — fator de geração esperada (kWh/kWp/dia)
            const { data: geradorasRow } = await supabase
                .from('system_settings').select('value')
                .eq('key', 'geradoras_fator_geracao_kwh_por_kwp_dia').maybeSingle();
            setGeradorasFatorGeracao(geradorasRow?.value || '4.5');
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveVoiceWebhook = async () => {
        setSaving(true);
        setSuccess(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'voice_transcription_webhook_url',
                    value: webhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveNegotiationWebhook = async () => {
        setSavingNeg(true);
        setSuccessNeg(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'n8n_negotiation_webhook_url',
                    value: negotiationWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessNeg(true);
            setTimeout(() => setSuccessNeg(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de negociação.');
        } finally {
            setSavingNeg(false);
        }
    };

    const handleSaveSimulationWebhook = async () => {
        setSavingSim(true);
        setSuccessSim(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'simulation_webhook_url',
                    value: simulationWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessSim(true);
            setTimeout(() => setSuccessSim(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de simulação.');
        } finally {
            setSavingSim(false);
        }
    };

    const handleSaveBoletoWebhook = async () => {
        setSavingBoleto(true);
        setSuccessBoleto(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'boleto_emission_webhook_url',
                    value: boletoWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessBoleto(true);
            setTimeout(() => setSuccessBoleto(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de emissão de boleto.');
        } finally {
            setSavingBoleto(false);
        }
    };

    const handleSaveBoletoSendWebhook = async () => {
        setSavingBoletoSend(true);
        setSuccessBoletoSend(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'boleto_send_webhook_url',
                    value: boletoSendWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessBoletoSend(true);
            setTimeout(() => setSuccessBoletoSend(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de envio de boleto ao cliente.');
        } finally {
            setSavingBoletoSend(false);
        }
    };

    const handleSaveRateioProtocoloWebhook = async () => {
        setSavingRateioProtocolo(true);
        setSuccessRateioProtocolo(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'rateio_protocolo_webhook_url',
                    value: rateioProtocoloWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessRateioProtocolo(true);
            setTimeout(() => setSuccessRateioProtocolo(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de protocolo de rateio.');
        } finally {
            setSavingRateioProtocolo(false);
        }
    };

    const handleSaveEmailPrincipalRateio = async () => {
        setSavingEmailPrincipalRateio(true);
        setSuccessEmailPrincipalRateio(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'rateio_protocolo_email_principal',
                    value: emailPrincipalRateio,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessEmailPrincipalRateio(true);
            setTimeout(() => setSuccessEmailPrincipalRateio(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar e-mail principal do protocolo de rateio.');
        } finally {
            setSavingEmailPrincipalRateio(false);
        }
    };

    const handleSaveRateioEspelhoWebhook = async () => {
        setSavingRateioEspelho(true);
        setSuccessRateioEspelho(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'rateio_espelho_webhook_url',
                    value: rateioEspelhoWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessRateioEspelho(true);
            setTimeout(() => setSuccessRateioEspelho(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de cobrança de espelhos de rateio.');
        } finally {
            setSavingRateioEspelho(false);
        }
    };

    const handleSaveFaturaSkipSendWebhook = async () => {
        setSavingFaturaSkipSend(true);
        setSuccessFaturaSkipSend(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'fatura_skip_send_webhook_url',
                    value: faturaSkipSendWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessFaturaSkipSend(true);
            setTimeout(() => setSuccessFaturaSkipSend(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de comunicação de fatura sem cálculo.');
        } finally {
            setSavingFaturaSkipSend(false);
        }
    };

    const handleSaveBoletoCancellationWebhook = async () => {
        setSavingBoletoCancel(true);
        setSuccessBoletoCancel(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'boleto_cancellation_webhook_url',
                    value: boletoCancellationWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessBoletoCancel(true);
            setTimeout(() => setSuccessBoletoCancel(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de cancelamento de boleto.');
        } finally {
            setSavingBoletoCancel(false);
        }
    };

    const handleSaveBoletoProrrogacaoWebhook = async () => {
        setSavingBoletoProrrogacao(true);
        setSuccessBoletoProrrogacao(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'boleto_prorrogacao_webhook_url',
                    value: boletoProrrogacaoWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessBoletoProrrogacao(true);
            setTimeout(() => setSuccessBoletoProrrogacao(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de prorrogação de boleto.');
        } finally {
            setSavingBoletoProrrogacao(false);
        }
    };

    // State for Document Upload Webhook
    const [documentUploadWebhookUrl, setDocumentUploadWebhookUrl] = useState('');
    const [savingDoc, setSavingDoc] = useState(false);
    const [successDoc, setSuccessDoc] = useState(false);

    const handleSaveDocumentUploadWebhook = async () => {
        setSavingDoc(true);
        setSuccessDoc(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'documents_upload_webhook_url',
                    value: documentUploadWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessDoc(true);
            setTimeout(() => setSuccessDoc(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de documentos.');
        } finally {
            setSavingDoc(false);
        }
    };

    const handleSaveContractWebhook = async () => {
        setSavingContract(true);
        setSuccessContract(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'issuance_of_terms_of_agreement',
                    value: contractWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessContract(true);
            setTimeout(() => setSuccessContract(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de emissão de termo.');
        } finally {
            setSavingContract(false);
        }
    };

    const handleSavePdfInvoiceWebhook = async () => {
        setSavingPdfInvoice(true);
        setSuccessPdfInvoice(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'pdf_invoice_webhook_url',
                    value: pdfInvoiceWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessPdfInvoice(true);
            setTimeout(() => setSuccessPdfInvoice(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de importação de fatura PDF.');
        } finally {
            setSavingPdfInvoice(false);
        }
    };

    const handleSaveCrmFaturaWebhook = async () => {
        setSavingCrmFatura(true);
        setSuccessCrmFatura(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'crm_fatura_upload_webhook_url',
                    value: crmFaturaWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessCrmFatura(true);
            setTimeout(() => setSuccessCrmFatura(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de importação de fatura do CRM.');
        } finally {
            setSavingCrmFatura(false);
        }
    };

    const handleSaveCrmDocumentoWebhook = async () => {
        setSavingCrmDocumento(true);
        setSuccessCrmDocumento(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'crm_documento_upload_webhook_url',
                    value: crmDocumentoWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessCrmDocumento(true);
            setTimeout(() => setSuccessCrmDocumento(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de envio de documento do CRM.');
        } finally {
            setSavingCrmDocumento(false);
        }
    };

    const handleSavePdfDefinitivoWebhook = async () => {
        setSavingPdfDefinitivo(true);
        setSuccessPdfDefinitivo(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'pdf_definitivo_webhook_url',
                    value: pdfDefinitivoWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessPdfDefinitivo(true);
            setTimeout(() => setSuccessPdfDefinitivo(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de substituição de PDF definitivo.');
        } finally {
            setSavingPdfDefinitivo(false);
        }
    };

    const handleSaveSicoobConsultaWebhook = async () => {
        setSavingSicoobConsulta(true);
        setSuccessSicoobConsulta(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'sicoob_consulta_webhook_url',
                    value: sicoobConsultaWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessSicoobConsulta(true);
            setTimeout(() => setSuccessSicoobConsulta(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de consulta Sicoob.');
        } finally {
            setSavingSicoobConsulta(false);
        }
    };

    const handleSaveSicoobConsultaPagadorWebhook = async () => {
        setSavingSicoobConsultaPagador(true);
        setSuccessSicoobConsultaPagador(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'sicoob_consulta_pagador_webhook_url',
                    value: sicoobConsultaPagadorWebhookUrl,
                    updated_at: new Date()
                });
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessSicoobConsultaPagador(true);
            setTimeout(() => setSuccessSicoobConsultaPagador(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de consulta por pagador Sicoob.');
        } finally {
            setSavingSicoobConsultaPagador(false);
        }
    };

    const handleSaveBoletoUnicoPagamentoEquatorialWebhook = async () => {
        setSavingBoletoUnicoPagamentoEquatorial(true);
        setSuccessBoletoUnicoPagamentoEquatorial(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'boleto_unico_pagamento_equatorial_webhook_url',
                    value: boletoUnicoPagamentoEquatorialWebhookUrl,
                    updated_at: new Date()
                });
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessBoletoUnicoPagamentoEquatorial(true);
            setTimeout(() => setSuccessBoletoUnicoPagamentoEquatorial(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de pagamento Equatorial (boleto único).');
        } finally {
            setSavingBoletoUnicoPagamentoEquatorial(false);
        }
    };

    const handleSaveAditivoWebhook = async () => {
        setSavingAditivo(true);
        setSuccessAditivo(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'aditivo_contratual_webhook_url',
                    value: aditivoWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessAditivo(true);
            setTimeout(() => setSuccessAditivo(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de aditivo contratual.');
        } finally {
            setSavingAditivo(false);
        }
    };

    const handleSaveLemitCusto = async () => {
        setSavingLemitCusto(true);
        setSuccessLemitCusto(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert([
                    { key: 'lemit_webhook_url', value: lemitWebhookUrl.trim(), updated_at: new Date() },
                    { key: 'lemit_custo_pessoa', value: lemitCustoPessoa, updated_at: new Date() },
                    { key: 'lemit_custo_empresa', value: lemitCustoEmpresa, updated_at: new Date() },
                    { key: 'lemit_limite_diario', value: lemitLimiteDiario.trim() || '500', updated_at: new Date() },
                    { key: 'prospeccao_janela_inicio', value: prospeccaoJanelaInicio.trim() || '22:00', updated_at: new Date() },
                    { key: 'prospeccao_janela_fim', value: prospeccaoJanelaFim.trim() || '23:59', updated_at: new Date() },
                ]);

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessLemitCusto(true);
            setTimeout(() => setSuccessLemitCusto(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração Lemit.');
        } finally {
            setSavingLemitCusto(false);
        }
    };

    const handleSaveCdd = async () => {
        setSavingCdd(true);
        setSuccessCdd(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert([
                    { key: 'casadosdados_custo_consulta', value: cddCusto, updated_at: new Date() },
                    { key: 'casadosdados_limite_mensal', value: cddLimite, updated_at: new Date() },
                ]);
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessCdd(true);
            setTimeout(() => setSuccessCdd(false), 3000);
        } catch (error) {
            console.error('Error saving Casa dos Dados settings:', error);
            alert('Erro ao salvar configuração Casa dos Dados.');
        } finally {
            setSavingCdd(false);
        }
    };

    const handleSaveGeradorasFator = async () => {
        setSavingGeradorasFator(true);
        setSuccessGeradorasFator(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert([
                    { key: 'geradoras_fator_geracao_kwh_por_kwp_dia', value: geradorasFatorGeracao, updated_at: new Date() },
                ]);
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessGeradorasFator(true);
            setTimeout(() => setSuccessGeradorasFator(false), 3000);
        } catch (error) {
            console.error('Error saving fator de geração:', error);
            alert('Erro ao salvar fator de geração das Geradoras.');
        } finally {
            setSavingGeradorasFator(false);
        }
    };

    const handleSaveMoraWebhook = async () => {
        setSavingMora(true);
        setSuccessMora(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'notificacao_mora_webhook_url',
                    value: moraWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessMora(true);
            setTimeout(() => setSuccessMora(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de notificação de mora.');
        } finally {
            setSavingMora(false);
        }
    };

    const handleSaveTrocaTitularidadeWebhook = async () => {
        setSavingTrocaTitularidade(true);
        setSuccessTrocaTitularidade(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'troca_titularidade_webhook_url',
                    value: trocaTitularidadeWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessTrocaTitularidade(true);
            setTimeout(() => setSuccessTrocaTitularidade(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de Troca de Titularidade.');
        } finally {
            setSavingTrocaTitularidade(false);
        }
    };

    const handleSaveAdvertenciaWebhook = async () => {
        setSavingAdvertencia(true);
        setSuccessAdvertencia(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'comunicado_advertencia_webhook_url',
                    value: advertenciaWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessAdvertencia(true);
            setTimeout(() => setSuccessAdvertencia(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de comunicado de advertência.');
        } finally {
            setSavingAdvertencia(false);
        }
    };

    const handleSaveCancellationWebhook = async () => {
        setSavingCancel(true);
        setSuccessCancel(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'cancellation_term_webhook_url',
                    value: cancellationWebhookUrl,
                    updated_at: new Date()
                });

            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'apis', acao: 'salvar_configuracao_api', origem: 'acao' });
            setSuccessCancel(true);
            setTimeout(() => setSuccessCancel(false), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Erro ao salvar configuração de emissão de termo de cancelamento.');
        } finally {
            setSavingCancel(false);
        }
    };

    return (
        <div className={`space-y-6 animate-fade-in ${aba === 'custos' ? 'max-w-7xl' : 'max-w-4xl'}`}>
            <div>
                <h2 className="text-xl font-bold font-poppins text-gray-900 dark:text-white">APIs e Webhooks</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {aba === 'custos' ? 'Quanto o sistema gasta com cada API paga.' : 'Gerencie integrações com sistemas externos.'}
                </p>
            </div>

            {/* Abas: Configurações (webhooks e chaves) | Custos (painel de gasto) */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
                <button
                    onClick={() => trocarAba('config')}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${aba === 'config' ? 'bg-white dark:bg-gray-700 shadow-sm font-semibold text-primary dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}
                >
                    <Settings2 size={14} /> Configurações
                </button>
                <button
                    onClick={() => trocarAba('custos')}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${aba === 'custos' ? 'bg-white dark:bg-gray-700 shadow-sm font-semibold text-primary dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}
                >
                    <Wallet size={14} /> Custos
                </button>
            </div>

            {aba === 'custos' ? (
                <Suspense fallback={<div className="flex items-center gap-2 py-16 justify-center text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Carregando painel de custos...</div>}>
                    <CustosApisView />
                </Suspense>
            ) : (
            <>
            {/* Voice Transcription Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                            <Mic size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Transcrição de Notas de Voz</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para processamento automático de áudio.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={webhookUrl}
                            onChange={(e) => setWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveVoiceWebhook}
                            disabled={saving}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${success
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {saving ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : success ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        O sistema enviará uma requisição POST com o JSON contendo
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">url_arquivo</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">nota_id</code> e
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">negociacao_id</code>.
                    </p>
                </div>
            </div>

            {/* Negotiation Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
                            <Kanban size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Criação de Negociação</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para envio de dados e documentos da negociação.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={negotiationWebhookUrl}
                            onChange={(e) => setNegotiationWebhookUrl(e.target.value)}
                            placeholder="https://your-n8n-instance.com/webhook/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveNegotiationWebhook}
                            disabled={savingNeg}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successNeg
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingNeg ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successNeg ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao criar uma negociação com "Deseja anexar documentos?" marcado.
                        Envia payload <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">multipart/form-data</code> com campos e arquivos.
                    </p>
                </div>
            </div>

            {/* Simulation Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg">
                            <Calculator size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Simulação de Economia</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para processar simulações de economia.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={simulationWebhookUrl}
                            onChange={(e) => setSimulationWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveSimulationWebhook}
                            disabled={savingSim}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successSim
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingSim ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successSim ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Receberá um array de objetos contendo os dados das unidades consumidoras selecionadas.
                    </p>
                </div>
            </div>

            {/* Document Upload Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg">
                            <FileText size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Envio de Documentos</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para receber arquivos de negociações.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={documentUploadWebhookUrl}
                            onChange={(e) => setDocumentUploadWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveDocumentUploadWebhook}
                            disabled={savingDoc}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successDoc
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingDoc ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successDoc ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Boleto Emission Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg">
                            <Banknote size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Emissão de Boleto</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook único — o n8n roteia internamente para Vigor Associação ou Vigor Gestão via nó IF.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={boletoWebhookUrl}
                            onChange={(e) => setBoletoWebhookUrl(e.target.value)}
                            placeholder="https://n8n.srv.../webhook/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveBoletoWebhook}
                            disabled={savingBoleto}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${successBoleto ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                            {savingBoleto ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successBoleto ? (
                                <><CheckCircle2 size={16} />Salvo!</>
                            ) : (
                                <><Save size={16} />Salvar</>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Envia o objeto completo da fatura em JSON, incluindo <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-0.5 text-gray-700 dark:text-gray-300">conta_emissao</code> (associacao, gestao ou hsolar) e <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-0.5 text-gray-700 dark:text-gray-300">emitir_em_vigor_gestao</code>. O n8n usa esses campos para decidir a conta SICOOB.
                    </p>
                </div>
            </div>

            {/* Boleto Send to Client Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                            <Send size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Envio de Boleto ao Cliente</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook acionado após confirmar os dados de envio do boleto para o cliente.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={boletoSendWebhookUrl}
                            onChange={(e) => setBoletoSendWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveBoletoSendWebhook}
                            disabled={savingBoletoSend}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successBoletoSend
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingBoletoSend ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successBoletoSend ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Receberá um JSON com os dados do cliente, mês de referência, valor do boleto, vencimento, linha digitável e código PIX.
                    </p>
                </div>
            </div>

            {/* Rateio Protocolo Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                            <Send size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Protocolo de Rateio à Distribuidora</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook acionado ao clicar em "Enviar Planilha" no Módulo de Rateio (Gestão &gt; Simulador de Rateio).</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={rateioProtocoloWebhookUrl}
                            onChange={(e) => setRateioProtocoloWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveRateioProtocoloWebhook}
                            disabled={savingRateioProtocolo}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successRateioProtocolo
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingRateioProtocolo ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successRateioProtocolo ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Receberá um JSON com a UG, distribuidora, mês de referência, a URL assinada da planilha gerada, o e-mail da distribuidora (cadastrado em Distribuidoras) e o e-mail principal abaixo. Sem esta URL configurada, o sistema apenas baixa a planilha localmente para envio manual.
                    </p>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 mt-4">
                        E-mail Principal (cópia interna do protocolo)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="email"
                            value={emailPrincipalRateio}
                            onChange={(e) => setEmailPrincipalRateio(e.target.value)}
                            placeholder="usuario@vigorenergy.com.br"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveEmailPrincipalRateio}
                            disabled={savingEmailPrincipalRateio}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successEmailPrincipalRateio
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingEmailPrincipalRateio ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successEmailPrincipalRateio ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Vai junto no payload como <code>usuario_principal_email</code> — quem sempre deve receber o protocolo, além da distribuidora.
                    </p>
                </div>
            </div>

            {/* Rateio Espelho Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                            <AlertOctagon size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Cobrança de Espelhos de Rateio</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook acionado automaticamente a cada 15 dias enquanto um rateio protocolado não tiver espelho registrado (Gestão &gt; Simulador de Rateio &gt; Conciliação).</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={rateioEspelhoWebhookUrl}
                            onChange={(e) => setRateioEspelhoWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveRateioEspelhoWebhook}
                            disabled={savingRateioEspelho}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successRateioEspelho
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingRateioEspelho ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successRateioEspelho ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Receberá um JSON com a UG, distribuidora e mês de referência do rateio pendente. Sem esta URL configurada, o sistema cria um lembrete in-app para os Gestores em vez de disparar o webhook.
                    </p>
                </div>
            </div>

            {/* Fatura Skip Send Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded-lg">
                            <Send size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Envio de Comunicação (Sem Cálculo)</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook acionado para comunicar faturas que ignoraram a etapa de cálculo.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={faturaSkipSendWebhookUrl}
                            onChange={(e) => setFaturaSkipSendWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveFaturaSkipSendWebhook}
                            disabled={savingFaturaSkipSend}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successFaturaSkipSend
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingFaturaSkipSend ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successFaturaSkipSend ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Receberá um JSON com os dados do cliente e informações afetas a fatura para se comunicar.
                    </p>
                </div>
            </div>

            {/* Boleto Cancellation Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
                            <Banknote size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Cancelamento de boletos</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para processar o cancelamento e baixa do boleto após emissão.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={boletoCancellationWebhookUrl}
                            onChange={(e) => setBoletoCancellationWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveBoletoCancellationWebhook}
                            disabled={savingBoletoCancel}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successBoletoCancel
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingBoletoCancel ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successBoletoCancel ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao confirmar o cancelamento no modal do boleto emitido. O sistema aguardará a resposta.
                    </p>
                </div>
            </div>

            {/* Boleto Prorrogação Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                            <Banknote size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Prorrogação de boletos</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para prorrogar a data limite de pagamento de um boleto já emitido (sem reemitir).</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={boletoProrrogacaoWebhookUrl}
                            onChange={(e) => setBoletoProrrogacaoWebhookUrl(e.target.value)}
                            placeholder="https://n8n.../webhook/vigorhub-prorrogar-boleto"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveBoletoProrrogacaoWebhook}
                            disabled={savingBoletoProrrogacao}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successBoletoProrrogacao
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingBoletoProrrogacao ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successBoletoProrrogacao ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao confirmar a prorrogação no modal do boleto emitido. Recebe <code>nossoNumero</code>, <code>nova_data_limite_pagamento</code>, <code>conta_sicoob</code> e <code>emitir_em_vigor_gestao</code>.
                    </p>
                </div>
            </div>

            {/* Sicoob Consulta Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light rounded-lg">
                            <Search size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Consulta de Boleto Sicoob</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para consultar boletos emitidos diretamente no Sicoob.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={sicoobConsultaWebhookUrl}
                            onChange={(e) => setSicoobConsultaWebhookUrl(e.target.value)}
                            placeholder="https://n8n.srv.../webhook/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveSicoobConsultaWebhook}
                            disabled={savingSicoobConsulta}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${successSicoobConsulta ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                            {savingSicoobConsulta ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successSicoobConsulta ? (
                                <><CheckCircle2 size={16} />Salvo!</>
                            ) : (
                                <><Save size={16} />Salvar</>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao usar "Consultar cobrança" no botão de Nova ação. Envia um JSON com
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">nossoNumero</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">linhaDigitavel</code> ou
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">codigoBarras</code>.
                        O n8n autentica no Sicoob (OAuth 2.0) e retorna o objeto <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">resultado</code>.
                    </p>
                </div>
            </div>

            {/* Sicoob Consulta Pagador Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light rounded-lg">
                            <User size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Consulta de Boletos por CPF/CNPJ</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para listar todos os boletos de um pagador via CPF/CNPJ.</p>
                        </div>
                    </div>
                </div>
                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={sicoobConsultaPagadorWebhookUrl}
                            onChange={(e) => setSicoobConsultaPagadorWebhookUrl(e.target.value)}
                            placeholder="https://n8n.srv.../webhook/consulta-boleto-pagador-sicoob"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveSicoobConsultaPagadorWebhook}
                            disabled={savingSicoobConsultaPagador}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${successSicoobConsultaPagador ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                            {savingSicoobConsultaPagador ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successSicoobConsultaPagador ? (
                                <><CheckCircle2 size={16} />Salvo!</>
                            ) : (
                                <><Save size={16} />Salvar</>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao usar "Consultar cobrança → Por CPF/CNPJ". Envia
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">numeroCpfCnpj</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">dataInicio</code> e
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">dataFim</code> (opcionais).
                        Retorna array de boletos do pagador ordenados por vencimento.
                    </p>
                </div>
            </div>

            {/* Boleto Único — Pagamento Equatorial Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg">
                            <ArrowRightLeft size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Boleto Único — Pagamento Equatorial</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook acionado automaticamente ao confirmar o pagamento de um boleto único, para repassar a parte da Equatorial.</p>
                        </div>
                    </div>
                </div>
                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={boletoUnicoPagamentoEquatorialWebhookUrl}
                            onChange={(e) => setBoletoUnicoPagamentoEquatorialWebhookUrl(e.target.value)}
                            placeholder="https://n8n.srv.../webhook/pagar-equatorial-boleto-unico"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveBoletoUnicoPagamentoEquatorialWebhook}
                            disabled={savingBoletoUnicoPagamentoEquatorial}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${successBoletoUnicoPagamentoEquatorial ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                            {savingBoletoUnicoPagamentoEquatorial ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successBoletoUnicoPagamentoEquatorial ? (
                                <><CheckCircle2 size={16} />Salvo!</>
                            ) : (
                                <><Save size={16} />Salvar</>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Chamado pela fila <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">pagamento_equatorial_fila</code> (processada a cada 2 min) para UCs aderidas ao boleto único. Envia
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">fatura_id</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">uc_codigo</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">valor_equatorial</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">linha_digitavel</code> e
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">pix_copia_cola</code>. Deve responder 200 em caso de sucesso (senão, o repasse é reenfileirado até esgotar as tentativas).
                    </p>
                </div>
            </div>

            {/* Contract Emission Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                            <ClipboardSignature size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Emissão de Termo de Adesão</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para gerar e enviar termos de adesão.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={contractWebhookUrl}
                            onChange={(e) => setContractWebhookUrl(e.target.value)}
                            placeholder="https://n8n.srv1179066.hstgr.cloud/webhook/8d1cf955-73fb-46f2-bf3c-ed36b4fbfa51"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveContractWebhook}
                            disabled={savingContract}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successContract
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingContract ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successContract ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao clicar em "Enviar termo". Envia os dados da negociação e UCs em JSON.
                    </p>
                </div>
            </div>

            {/* Termination Term Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
                            <FileText size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Emissão de Termo de Cancelamento</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para gerar e enviar termos de distrato.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={cancellationWebhookUrl}
                            onChange={(e) => setCancellationWebhookUrl(e.target.value)}
                            placeholder="https://n8n.srv1179066.hstgr.cloud/webhook/distrato-vigor-energy"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveCancellationWebhook}
                            disabled={savingCancel}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successCancel
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingCancel ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successCancel ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao gerar distrato em Financeiro. Envia dados da UC incluindo
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">modelo_distrato</code>
                        (<code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300">"padrao"</code> ou <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300">"inadimplencia"</code>).
                        Retorna <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">link_assinatura_cliente</code> e <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300">numero_termo</code>.
                    </p>
                </div>
            </div>

            {/* Comunicado de Advertência Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                            <FileWarning size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Comunicado de Advertência</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para gerar a notificação extrajudicial por violação de exclusividade.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={advertenciaWebhookUrl}
                            onChange={(e) => setAdvertenciaWebhookUrl(e.target.value)}
                            placeholder="https://n8n.seuservidor.com/webhook/comunicado-advertencia-vigor-energy"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveAdvertenciaWebhook}
                            disabled={savingAdvertencia}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successAdvertencia
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingAdvertencia ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successAdvertencia ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao gerar advertência em Financeiro &gt; UCs. Envia dados do associado e da UC notificada.
                        Retorna <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">link_assinatura_cliente</code> e <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300">numero_termo</code>.
                    </p>
                </div>
            </div>

            {/* Notificação de Mora Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg">
                            <AlertOctagon size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Notificação de Mora</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para gerar a notificação extrajudicial de cobrança e constituição em mora.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={moraWebhookUrl}
                            onChange={(e) => setMoraWebhookUrl(e.target.value)}
                            placeholder="https://n8n.seuservidor.com/webhook/notificacao-mora-vigor-energy"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveMoraWebhook}
                            disabled={savingMora}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successMora
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingMora ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successMora ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao notificar mora em Financeiro &gt; UCs. Envia dados do notificado, vencimentos e valor total do débito.
                        Retorna <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">link_assinatura_cliente</code> e <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300">numero_termo</code>.
                    </p>
                </div>
            </div>

            {/* Troca de Titularidade Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 text-primary rounded-lg">
                            <ArrowRightLeft size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Declaração de Troca de Titularidade (Equatorial)</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para gerar a declaração de troca de titularidade quando a Validação de Dados sinalizar a necessidade.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={trocaTitularidadeWebhookUrl}
                            onChange={(e) => setTrocaTitularidadeWebhookUrl(e.target.value)}
                            placeholder="https://n8n.seuservidor.com/webhook/troca-titularidade-vigor-energy"
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveTrocaTitularidadeWebhook}
                            disabled={savingTrocaTitularidade}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successTrocaTitularidade
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingTrocaTitularidade ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successTrocaTitularidade ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado pela Edge Function <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">gerar-troca-titularidade</code> ao clicar em "Enviar Documento de Troca de Titularidade" na negociação.
                        Só o cliente assina (sem procurador/testemunhas). Retorna <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">link_assinatura_cliente</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">numero_termo</code> e <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300">id_autentique</code>.
                    </p>
                </div>
            </div>

            {/* PDF Invoice Upload Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
                            <Upload size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Importação da Fatura (PDF)</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para processamento de faturas enviadas em PDF.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={pdfInvoiceWebhookUrl}
                            onChange={(e) => setPdfInvoiceWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSavePdfInvoiceWebhook}
                            disabled={savingPdfInvoice}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successPdfInvoice
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingPdfInvoice ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successPdfInvoice ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao enviar um arquivo PDF na tela de Fatura. Envia o arquivo no payload para processamento.
                    </p>
                </div>
            </div>

            {/* CRM Invoice Upload Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
                            <Upload size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Importação de Fatura (PDF) — CRM</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para processamento de faturas enviadas em PDF pelo funil de negociações (CRM).</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={crmFaturaWebhookUrl}
                            onChange={(e) => setCrmFaturaWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveCrmFaturaWebhook}
                            disabled={savingCrmFatura}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successCrmFatura
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingCrmFatura ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successCrmFatura ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao clicar em "Enviar fatura" no botão de ações do Kanban (CRM). Envia o arquivo no payload para processamento.
                    </p>
                </div>
            </div>

            {/* CRM Documento Upload Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                            <Upload size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Envio de Documento — CRM</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para processamento de documentos enviados pelo funil de negociações (CRM).</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={crmDocumentoWebhookUrl}
                            onChange={(e) => setCrmDocumentoWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveCrmDocumentoWebhook}
                            disabled={savingCrmDocumento}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successCrmDocumento
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingCrmDocumento ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successCrmDocumento ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao clicar em "Enviar documento" no botão de ações do Kanban (CRM). Envia o arquivo no payload para processamento.
                    </p>
                </div>
            </div>

            {/* PDF Definitivo Upload Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors mt-6">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-lg">
                            <Upload size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Substituição de PDF Definitivo da Fatura</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook acionado ao substituir o espelho pela fatura definitiva.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={pdfDefinitivoWebhookUrl}
                            onChange={(e) => setPdfDefinitivoWebhookUrl(e.target.value)}
                            placeholder="https://hook.us1.make.com/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSavePdfDefinitivoWebhook}
                            disabled={savingPdfDefinitivo}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successPdfDefinitivo
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingPdfDefinitivo ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successPdfDefinitivo ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Recebe o arquivo PDF e o ID da fatura para salvar no Vercel Blob e atualizar a fatura.
                    </p>
                </div>
            </div>

            {/* Aditivo Contratual Webhook Card */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                            <FilePlus2 size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Aditivo Contratual</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook para gerar e enviar termos aditivos de transferência de unidade.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL do Webhook (n8n / Zapier / Make)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={aditivoWebhookUrl}
                            onChange={(e) => setAditivoWebhookUrl(e.target.value)}
                            placeholder="https://n8n.seuservidor.com/webhook/..."
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <button
                            onClick={handleSaveAditivoWebhook}
                            disabled={savingAditivo}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all
                                ${successAditivo
                                    ? 'bg-warning text-white hover:bg-warning/90'
                                    : 'bg-primary text-white hover:bg-primary/90'
                                }
                            `}
                        >
                            {savingAditivo ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successAditivo ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    Salvo!
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Salvar
                                </>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Acionado ao gerar aditivo contratual em Financeiro. Payload inclui
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">titular_atual_nome</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">novo_titular_nome</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">novo_titular_cpf_cnpj</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">uc01</code>,
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">data_transferencia</code>, e-mail e WhatsApp.
                        Retorna <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-1 text-gray-700 dark:text-gray-300">link_assinatura_cliente</code> e <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300">numero_termo</code>.
                    </p>
                </div>
            </div>

            {/* Lemit — Custo por consulta (Central de Custos) */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#5F6C37]/10 text-[#5F6C37] dark:text-emerald-400 rounded-lg">
                            <Sparkles size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Enriquecimento Lemit</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Webhook proxy (n8n, IP fixo) e custo por consulta usado na Central de Custos.</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL do Webhook (proxy n8n)</label>
                        <input
                            type="url"
                            value={lemitWebhookUrl}
                            onChange={(e) => setLemitWebhookUrl(e.target.value)}
                            placeholder="https://n8n.seuservidor/webhook/lemit-proxy"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            A Edge Function roteia a Lemit por aqui (IP fixo cadastrado na allowlist). Deixe vazio para chamar a Lemit direto (fallback).
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF / Pessoa (R$)</label>
                            <input
                                type="number" step="0.01" min="0"
                                value={lemitCustoPessoa}
                                onChange={(e) => setLemitCustoPessoa(e.target.value)}
                                placeholder="0,00"
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CNPJ / Empresa (R$)</label>
                            <input
                                type="number" step="0.01" min="0"
                                value={lemitCustoEmpresa}
                                onChange={(e) => setLemitCustoEmpresa(e.target.value)}
                                placeholder="0,00"
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                            />
                        </div>
                    </div>

                    {/* Fila noturna da Prospecção (Lista Fria) */}
                    <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-bold text-gray-800 dark:text-gray-100">Enriquecimento noturno (Lista Fria)</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
                            Os lotes da Prospecção rodam no servidor, dentro desta janela, gastando só o que <b>sobrou</b> do teto
                            do dia — as consultas manuais do time durante o expediente entram na mesma conta. Mudar aqui vale
                            imediatamente, inclusive para lotes já enfileirados.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teto diário (consultas)</label>
                                <input
                                    type="number" step="1" min="0"
                                    value={lemitLimiteDiario}
                                    onChange={(e) => setLemitLimiteDiario(e.target.value)}
                                    placeholder="500"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Começa às</label>
                                <input
                                    type="time"
                                    value={prospeccaoJanelaInicio}
                                    onChange={(e) => setProspeccaoJanelaInicio(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Termina às</label>
                                <input
                                    type="time"
                                    value={prospeccaoJanelaFim}
                                    onChange={(e) => setProspeccaoJanelaFim(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end mt-4">
                        <button
                            onClick={handleSaveLemitCusto}
                            disabled={savingLemitCusto}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${successLemitCusto ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                            {savingLemitCusto ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successLemitCusto ? (
                                <><CheckCircle2 size={16} />Salvo!</>
                            ) : (
                                <><Save size={16} />Salvar</>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        O token da Lemit é mantido como secret no servidor (Supabase Edge Function <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-0.5 text-gray-700 dark:text-gray-300">LEMIT_API_TOKEN</code>), não aqui.
                    </p>
                </div>
            </div>

            {/* Casa dos Dados — Descoberta de CNPJ (Prospecção Geográfica) */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-violet-500/10 text-violet-600 dark:text-violet-400 rounded-lg">
                            <Search size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">Descoberta de CNPJ (Casa dos Dados)</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Busca reversa nome/cidade → CNPJ na Prospecção Geográfica. Custo e teto mensal de gasto.</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase">Gasto no mês</p>
                        <p className="text-lg font-bold text-violet-600 dark:text-violet-400">
                            {cddGastoMes == null ? '—' : `R$ ${cddGastoMes.toFixed(2).replace('.', ',')}`}
                        </p>
                    </div>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Custo por consulta (R$)</label>
                            <input
                                type="number" step="0.01" min="0"
                                value={cddCusto}
                                onChange={(e) => setCddCusto(e.target.value)}
                                placeholder="0,01"
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teto de gasto no mês (R$)</label>
                            <input
                                type="number" step="1" min="0"
                                value={cddLimite}
                                onChange={(e) => setCddLimite(e.target.value)}
                                placeholder="0 = sem limite"
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={handleSaveCdd}
                            disabled={savingCdd}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${successCdd ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                            {savingCdd ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successCdd ? (
                                <><CheckCircle2 size={16} />Salvo!</>
                            ) : (
                                <><Save size={16} />Salvar</>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Ao atingir o teto mensal, a descoberta é bloqueada até o próximo mês. A chave da API é mantida como secret no servidor (Supabase Edge Function <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded mx-0.5 text-gray-700 dark:text-gray-300">CASADOSDADOS_API_KEY</code>), não aqui.
                    </p>
                </div>
            </div>

            {/* Geradoras — Fator de Geração Esperada (Painel Executivo) */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                    <div className="p-2 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-lg">
                        <Calculator size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-800 dark:text-white">Fator de Geração Esperada (Painel Executivo)</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">kWh gerado por kWp instalado por dia — usado no comparativo "Real vs. Esperado" da seção Usinas Geradoras.</p>
                    </div>
                </div>
                <div className="p-6">
                    <div className="max-w-xs">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fator (kWh/kWp/dia)</label>
                        <input
                            type="number" step="0.1" min="0"
                            value={geradorasFatorGeracao}
                            onChange={(e) => setGeradorasFatorGeracao(e.target.value)}
                            placeholder="4.5"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 text-sm"
                        />
                    </div>
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={handleSaveGeradorasFator}
                            disabled={savingGeradorasFator}
                            className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all ${successGeradorasFator ? 'bg-warning text-white hover:bg-warning/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                        >
                            {savingGeradorasFator ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : successGeradorasFator ? (
                                <><CheckCircle2 size={16} />Salvo!</>
                            ) : (
                                <><Save size={16} />Salvar</>
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Referência típica para Goiás: ~4,5 kWh/kWp/dia. Se este campo nunca for salvo, o painel usa 4,5 automaticamente.
                    </p>
                </div>
            </div>

            <ParcelamentoConfigCard />

            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 text-center space-y-4 shadow-sm opacity-60">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-full w-fit mx-auto text-gray-400">
                    <Lock size={24} />
                </div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Mais integrações em breve</h3>
            </div>
            </>
            )}
        </div>
    );
};

export default ApisView;
